import { useEffect, useState } from "react"
import moment from "moment"
import { CosmosButton, CosmosCheckbox, CosmosIconErrorFilled, CosmosInput, CosmosOption, CosmosSelect, CosmosText } from "@cosmos/web/react"

// Guest form component that can be used for both adding and editing guests
export default function GuestForm({ guest, eventDates, eventGroups, redemptionTypes, checkIn, setCheckIn, onSave, onCancel, allGuests }) {
	// Form state
	const [firstName, setFirstName] = useState(guest?.first_name || "")
	const [lastName, setLastName] = useState(guest?.last_name || "")
	const [voucher, setVoucher] = useState(guest?.voucher_received || false)
	const [group, setGroup] = useState(guest?.event_group_id || 0)
	const [additionalGift, setAdditionalGift] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [errors, setErrors] = useState({})
	const [existingWarning, setExistingWarning] = useState(false)

	// get the keys of eventDates as an array
	const eventDateKeys = Object.keys(eventDates)
	// if current date is within eventDateKeys, set allowCheckIn to true
	const allowCheckIn = eventDateKeys.includes(moment().format("DD/MM/YYYY"))

	const [allowVoucher, setAllowVoucher] = useState(false)

	// When editing, check if voucher should be allowed
	useEffect(() => {
		// If check in times are provided, set allowVoucher to true
		if (guest?.checked_in) {
			setAllowVoucher(true)
		}
	}, [guest])

	const today = moment().format("DD/MM/YYYY")
	const inEvent = Object.keys(eventDates).some((date) => {
		if (date === today) return true
	})

	// Check for duplicate names
	const checkForExistingName = (firstName, lastName) => {
		if (firstName.trim() === "" || lastName.trim() === "") {
			return false
		}

		return allGuests.some((g) => {
			// If we're editing, exclude the current guest from the check
			if (guest && g.id === guest.id) {
				return false
			}
			return g.first_name === firstName && g.last_name === lastName
		})
	}

	const handleFirstNameChange = (firstName) => {
		setFirstName(firstName)
		setExistingWarning(checkForExistingName(firstName, lastName))
	}

	const handleLastNameChange = (lastName) => {
		setLastName(lastName)
		setExistingWarning(checkForExistingName(firstName, lastName))
	}

	const handleSubmit = () => {
		setIsSubmitting(true)
		let formErrors = {}

		if (!firstName) {
			formErrors["firstName"] = "First name cannot be empty"
		}

		if (!lastName) {
			formErrors["lastName"] = "Last name cannot be empty"
		}

		if (!group || group < 1) {
			formErrors["group"] = "Group cannot be empty"
		}

		if (Object.keys(formErrors).length > 0) {
			setErrors(formErrors)
			setIsSubmitting(false)
			return
		}

		// Call the onSave function with the form data
		onSave({
			id: guest?.id,
			firstName,
			lastName,
			voucher,
			group,
			additionalGift,
		})
			.then(() => {
				setIsSubmitting(false)
			})
			.catch(() => {
				setIsSubmitting(false)
			})
	}

	return (
		<div className="thosp-add-guest-form">
			<CosmosInput
				appearance="light"
				label="First name:"
				disabled={guest}
				invalid={errors.firstName ? true : null}
				validationMessage={errors.firstName}
				value={firstName}
				onInputinput={({ detail }) => handleFirstNameChange(detail.value)}
			/>

			<CosmosInput
				appearance="light"
				label="Last name:"
				disabled={guest}
				invalid={errors.lastName ? true : null}
				validationMessage={errors.lastName}
				value={lastName}
				onInputinput={({ detail }) => handleLastNameChange(detail.value)}
			/>

			<CosmosSelect
				appearance="light"
				label="Group:"
				className="span-2"
				invalid={errors.group ? true : null}
				validationMessage={errors.group}
				value={group}
				onInputchange={({ detail }) => setGroup(detail.value)}>
				{eventGroups?.map((group) => (
					<CosmosOption
						key={group.id}
						value={group.id}>
						{group.name}
					</CosmosOption>
				))}
			</CosmosSelect>

			{guest && (
				<div className="span-2 check-guest-in">
					<CosmosCheckbox
						appearance="light"
						checked={voucher}
						disabled={!allowVoucher || guest.voucher_received}
						onInputchange={({ detail }) => setVoucher(detail.checked)}>
						Voucher provided
					</CosmosCheckbox>

					<CosmosText
						appearance="light"
						size="x-small"
						kind="subtle">
						{guest.voucher_received && `Voucher provided on ${moment(guest.voucher_received_date).format("DD/MM/YYYY")} by ${guest.voucher_issued_by}`}
					</CosmosText>

					<CosmosText
						appearance="light"
						size="x-small"
						kind="subtle">
						{!allowVoucher && "To mark the voucher as provided, the guest must be checked in."}
					</CosmosText>
				</div>
			)}

			{!guest && (
				<>
					<div className="span-2 check-guest-in">
						<CosmosCheckbox
							appearance="light"
							checked={checkIn}
							disabled={!allowCheckIn}
							onInputchange={({ detail }) => setCheckIn(detail.checked)}>
							Check in now
						</CosmosCheckbox>

						<CosmosText
							appearance="light"
							size="x-small"
							kind="subtle">
							{!inEvent ? "You cannot check in a guest before the event starts" : "They will be checked in and can attend any event days"}
						</CosmosText>

						<br />

						<CosmosCheckbox
							appearance="light"
							checked={voucher}
							disabled={!checkIn}
							onInputchange={({ detail }) => setVoucher(detail.checked)}>
							Voucher provided
						</CosmosCheckbox>

						<CosmosText
							appearance="light"
							size="x-small"
							kind="subtle">
							{!checkIn && "To mark the voucher as provided, the guest must be checking in"}
						</CosmosText>
					</div>

					{redemptionTypes.length > 0 && (
						<div className="span-2 add-guest-gift">
							<CosmosCheckbox
								appearance="light"
								disabled={!checkIn}
								checked={additionalGift}
								onInputchange={({ detail }) => setAdditionalGift(detail.checked)}>
								{redemptionTypes.map((gift) => gift.description)} gift received
							</CosmosCheckbox>

							<CosmosText
								appearance="light"
								size="x-small"
								kind="subtle">
								Can only collect their gift if checking in now
							</CosmosText>
						</div>
					)}
				</>
			)}

			{existingWarning && (
				<CosmosText
					className="span-2 warningIcon"
					appearance="light"
					size="small">
					<CosmosIconErrorFilled className="warningIcon" /> A guest with the same name already exists!
				</CosmosText>
			)}

			<div className="span-2 add-guest-buttons">
				<CosmosButton
					appearance="light"
					shape="rectangle"
					size="small"
					kind="secondary"
					onClick={onCancel}>
					Cancel
				</CosmosButton>

				<CosmosButton
					appearance="light"
					shape="rectangle"
					size="small"
					busy={isSubmitting}
					onClick={handleSubmit}>
					{guest ? "Save Changes" : "Add Guest"}
				</CosmosButton>
			</div>

			{isSubmitting && (
				<div className="span-2 adding-guest-text">
					<CosmosText
						appearance="light"
						size="x-small"
						kind="subtle">
						{!guest ? "Adding guest, please wait..." : "Saving changes, please wait..."}
					</CosmosText>
				</div>
			)}
		</div>
	)
}
